import vr from "../../../utilFunctions/validationRules";

const { lenBetween,isEmpty } = vr;

export const buildAssetValidation = (t) => ({
    name: {
        required: true,
        requiredMessage: t.common.required_field,
        invalidMessage: t?.common?.minmax_chars?.replace?.("{min}", "3")?.replace?.("{max}", "150"),
        pattern: (v) => lenBetween(v, 3, 150),
        error: false,
        skipped: false,
    },

    serialNumber: {
        required: true,
        requiredMessage: t.common.required_field,
        invalidMessage:
            t.assets?.form?.invalid_serial_number || t.common.invalid_value,
        pattern: (v) => !!v && lenBetween(v, 1, 150),
        error: false,
        skipped: false,
    },

    qrcode: {
        required: true,
        requiredMessage: t.common.required_field,
        invalidMessage: t.common.invalid_value,
        pattern: (v) => typeof v === "boolean",
        error: false,
        skipped: false,
    },

    barcode: {
        required: true,
        requiredMessage: t.common.required_field,
        invalidMessage: t.common.invalid_value,
        pattern: (v) => typeof v === "boolean",
        error: false,
        skipped: false,
    },
    status: {
        required: false,
        requiredMessage: t.common.required_field,
        invalidMessage: t.common.invalid_value,
        pattern: (v) => typeof v === "string" && v.trim() !== "",
        error: false,
        skipped: false,
    },
    purchasePrice: {
        required: true,
        requiredMessage: t.common.required_field,
        invalidMessage:
            t.assets?.form?.invalid_purchase_price || t.common.invalid_value,
        pattern: (v) => {
            if (isEmpty(v)) return false;
            const n = Number(v);
            return !Number.isNaN(n) && n >= 0;
        },
        error: false,
        skipped: false,
    },
    location: {
        required: true,
        requiredMessage: t.common.required_field,
        invalidMessage: t.common.required_field,
        pattern: (v) => v !== "" && !isEmpty(v),
        error: false,
        skipped: false,
    },
    category: {
        required: true,
        requiredMessage: t.common.required_field,
        invalidMessage: t.common.required_field,
        pattern: (v) => v !== "" && !isEmpty(v),
        error: false,
        skipped: false,
    },
    parent_asset: {
        required: true,
        requiredMessage: t.common.required_field,
        invalidMessage: t.common.required_field,
        pattern: (v) => v !== "" && !isEmpty(v),
        error: false,
        skipped: false,
    },
});

export const computeValidation = (
    value,
    rule,
    requiredFallback = "Required"
) => {
    const empty = isEmpty(value);
    const passes = rule.pattern ? rule.pattern(value) : true;

    const error = rule.required
        ? empty || (!empty && !passes)
        : !empty && !passes;

    const message =
        empty && rule.required
            ? rule.requiredMessage ?? requiredFallback
            : !passes
                ? rule.invalidMessage ?? rule.message ?? "Invalid value"
                : "";

    return { error, message, skipped: true };
};

export const makeOnBlurFactory = ({ getAssetData, setValidation, t }) => {
    return (key, valueSelector = (cd) => cd[key]) => () => {
        setValidation((prev) => {
            const rule = prev[key];
            if (!rule) return prev;

            const value = valueSelector(getAssetData());
            return {
                ...prev,
                [key]: {
                    ...rule,
                    ...computeValidation(value, rule, t.common.required_field),
                },
            };
        });
    };
};

export const makeOnValuesChangeFactory =
    ({ getAssetData, setAssetData, setValidation, t }) =>
        (name) =>
            (event, value) => {
                const dropdownFields = ["status", "location", "category", "parentAsset"];
                const booleanFields = ["qrcode", "barcode"];
                const numericFields = ["purchasePrice"];

                let nextValue;

                if (booleanFields.includes(name)) {
                    nextValue = value?.checked ?? false;
                } else if (dropdownFields.includes(name)) {
                    nextValue = value?.value;
                } else if (numericFields.includes(name)) {
                    nextValue = event?.target?.value;
                } else {
                    nextValue = event?.target?.value;
                }

                setAssetData((prev) => ({
                    ...prev,
                    [name]: nextValue,
                }));

                setValidation((prev) => {
                    const next = { ...prev };
                    const vKey =
                        name === "parentAsset" ? "parent_asset" : name;

                    const rule = next[vKey];
                    if (!rule) return prev;

                    const res = computeValidation(
                        nextValue,
                        rule,
                        t.common.required_field
                    );

                    next[vKey] = { ...rule, ...res };
                    return next;
                });
            };

export const makeValidateOnSubmitFactory =
    ({ getAssetData, validation, setValidation, t }) =>
        () => {
            const data = getAssetData();

            const requiredKeys = [
                "name",
                "serialNumber",
                "qrcode",
                "barcode",
                "status",
                "purchasePrice",
                "location",
                "category",
            ];

            const next = { ...validation };

            requiredKeys.forEach((k) => {
                const value =
                    k === "location"
                        ? data.location
                        : k === "category"
                            ? data.category
                            : k === "purchasePrice"
                                ? data.purchasePrice
                                : data[k];

                const res = computeValidation(
                    value,
                    next[k],
                    t.common.required_field
                );

                next[k] = { ...next[k], ...res };
            });

            if (data.isChild) {
                const value = data.parentAsset;
                const res = computeValidation(
                    value,
                    next.parent_asset,
                    t.common.required_field
                );

                next.parent_asset = { ...next.parent_asset, ...res };
            } else {
                next.parent_asset = {
                    ...next.parent_asset,
                    error: false,
                    skipped: true,
                    message: "",
                };
            }

            setValidation(next);

            return Object.values(next).some((r) => r?.error);
        };
